import { ExternalLink } from "@/components/ui/icons";
import { useId } from "react";
import { Link } from "react-aria-components/Link";
import { Text, View } from "react-native";

import { PROVIDER_LOCKUPS } from "@/components/brand/provider-logos";
import { MarketplaceSearchFeedback } from "@/components/market/MarketplaceSearchFeedback";
import { ProviderLockup } from "@/components/market/PriceStack";
import { useMarketplaceSearch } from "@/hooks/useMarketplaceSearch";
import type { AltSoldsSearch } from "@/lib/scan/altSolds";
import type { FanaticsSoldsSearch } from "@/lib/scan/fanaticsSolds";
import { useThemeColors } from "@/theme/theme";

/** Opens marketplace sold history in a new tab beside the current card. */
export function MarketplaceSoldsLink({ search, testID, track = true, inline = false }: {
  search: FanaticsSoldsSearch | AltSoldsSearch;
  testID: string;
  /** Public pages disable authenticated analytics. */
  track?: boolean;
  /** A compact link inside the sales table. */
  inline?: boolean;
}) {
  const c = useThemeColors();
  const hintId = useId();
  const feedback = useMarketplaceSearch({
    marketplace: search.marketplace === "ALT" ? "alt" : "fanatics",
    url: search.url,
    track,
    properties: {
      search_query: search.query,
      grading_company: search.gradingService,
      grade_filter_applied: search.marketplace === "ALT",
      grade: "grade" in search ? search.grade : undefined,
      card_state: search.gradingService === "ungraded" ? "raw" : "graded",
      platform: "web",
      surface: testID.startsWith("browse-")
        ? "public_card"
        : testID.endsWith("card-detail")
        ? "card_detail"
        : "scan",
    },
  });
  return (
    <View>
      <Link
        href={search.url}
        target="_blank"
        rel="noopener noreferrer"
        onPress={() => {
          feedback.start();
        }}
        data-testid={testID}
        aria-label={`Search ${search.marketplace} sold items for ${search.name}`}
        aria-describedby={hintId}
        className={inline
          ? "flex no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary min-h-9 flex-row items-center gap-2 active:opacity-70"
          : "flex no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary min-h-12 flex-row items-center gap-3 rounded-lg border border-border bg-surface px-3 active:bg-surface-muted"}
      >
        <View className={inline ? "items-start gap-0.5" : "flex-1 gap-0.5 py-2"}>
          <View className="flex-row items-center gap-1.5">
            <Text className="font-label text-[13px] text-primary">Find it on</Text>
            <ProviderLockup
              logo={PROVIDER_LOCKUPS[search.marketplace === "ALT" ? "alt" : "fanatics"]}
              height={search.marketplace === "ALT" ? 26 : 18}
              label={search.marketplace}
              tintColor={search.marketplace === "Fanatics" ? c.foreground : undefined}
            />
          </View>
          <Text nativeID={hintId} className="text-xs text-muted-foreground">{search.hint}</Text>
        </View>
        <ExternalLink aria-hidden size={14} color={c.primary} />
      </Link>
      {feedback.failed
        ? (
          <Text accessibilityRole="alert" className="py-2 text-xs text-loss">
            Couldn’t open {search.marketplace}. Try the link again.
          </Text>
        )
        : null}
      {feedback.pending
        ? (
          <MarketplaceSearchFeedback
            marketplace={search.marketplace}
            onRespond={feedback.respond}
          />
        )
        : null}
    </View>
  );
}
